// Search-and-pick an existing thing (a class, a property, an entity) by its
// name. The one control for referring to a term: the entity page's statement
// editors, a fact's context and the assistant's aliases all use it, so nobody
// types an identifier where a name will do.
import { useMemo, useState } from 'react';
import { corePredicatesMatching } from '@/lib/coreLanguage';
import { useDebounced } from '@/lib/useDebounced';
import { localName } from '@/lib/useOntology';
import type { UriLabel } from '@/lib/useEntity';
import {
  searchTermFromInput,
  useVdbSearch,
  useVdbStatus,
  type VdbSearchKind,
  type VdbSearchResult,
} from '@/lib/useVdb';
import { buttonVariants } from '@/components/ui/Button';
import { fieldClass } from '@/components/ui/field';
import { cn } from '@/lib/utils';

export function EntityPicker({
  selected,
  onSelect,
  placeholder,
  kind,
  label,
  onTextChange,
}: {
  selected: UriLabel | null;
  onSelect: (sel: UriLabel | null) => void;
  placeholder: string;
  kind?: VdbSearchKind;
  /** The accessible name of the search box, where no visible label names it. */
  label?: string;
  /** The text typed and not yet picked, for a form that must not drop it unseen. */
  onTextChange?: (text: string) => void;
}) {
  const [text, setTextState] = useState('');
  const setText = (next: string) => {
    setTextState(next);
    onTextChange?.(next);
  };
  // Address-aware: `useVdbSearch` strips a prefix marker before querying, so
  // `:Person` finds the class. The local gate must use the same normalised
  // text or a short address never reaches the hook.
  const searchText = searchTermFromInput(text);
  const debounced = useDebounced(searchText, 200);
  const results = useVdbSearch(debounced, 12, false, kind);
  const status = useVdbStatus().data;
  const indexCurrent = status?.state === 'ready' && !status.waiting;
  // A property search finds what the ontology declares; the core language's
  // annotation predicates are offered beside it, since no ontology declares
  // them and an alternative label or a comment is added through them.
  const list = useMemo(() => {
    const found = results.data ?? [];
    if (kind !== 'property') return found;
    const core = corePredicatesMatching(searchText).filter((c) => !found.some((f) => f.uri === c.uri));
    return [...core.map((c) => ({ uri: c.uri, label: c.label, entity_type: 'Property', score: 1, graph: '', classes: [] })), ...found];
  }, [results.data, kind, searchText]);

  if (selected) {
    return (
      <span
        className="inline-flex max-w-full items-center gap-1 rounded-md border border-input bg-muted px-2 py-1 text-sm"
        title={selected.uri}
      >
        <span className="truncate">{selected.label}</span>
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'shrink-0')}
          aria-label={`Clear ${selected.label}`}
        >
          ×
        </button>
      </span>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        aria-label={label ?? placeholder}
        className={fieldClass('normal', 'w-full py-1')}
      />
      {searchText.length >= 3 && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-card shadow-lg">
          {results.isLoading && <div className="px-2 py-1 text-xs text-tres-text-secondary">Searching…</div>}
          {!results.isLoading && list.length === 0 && (
            <div className="px-2 py-1 text-xs text-tres-text-secondary">
              {indexCurrent
                ? 'Nothing by that name. Check the spelling, or create it first.'
                : 'Nothing by that name yet. The search is taking in a recent change; a name added in the last minutes appears here when it finishes.'}
            </div>
          )}
          {list.map((r: VdbSearchResult) => (
            <button
              key={r.uri}
              type="button"
              onClick={() => {
                onSelect({ uri: r.uri, label: r.label ?? localName(r.uri) });
                // The pick itself answers the typed text; reporting the cleared
                // box as well would hand the form a draft from before the pick.
                setTextState('');
              }}
              className="block w-full px-2 py-1 text-left text-sm hover:bg-muted"
              title={r.uri}
            >
              <span className="text-tres-text-primary">{r.label ?? localName(r.uri)}</span>
              <span className="ml-1 text-[10px] text-tres-text-secondary">{r.entity_type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
